import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { normalizeApiError } from '../../api/apiError';
import {
  getEmployeeLeaveBalancesAdmin,
  updateEmployeeLeaveBalance,
  validateLeaveBalanceAdjustment,
} from '../../services/leaveBalanceAdminService';
import type { AdminLeaveBalance, LeaveBalanceEmployeeResult } from '../../types/leaveBalanceAdmin';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatLeaveBalanceValue } from '../../utils/leaveSession';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveBalanceUpdate'>;

export function LeaveBalanceUpdateScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const lock = useRef(false);
  const [data, setData] = useState<LeaveBalanceEmployeeResult | null>(null);
  const [balance, setBalance] = useState<AdminLeaveBalance | null>(null);
  const [availableBalance, setAvailableBalance] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getEmployeeLeaveBalancesAdmin(
        route.params.employeeId,
        route.params.leaveYear,
      );
      const selected =
        result.balances.find(item => item.leaveTypeId === route.params.leaveTypeId) || null;
      setData(result);
      setBalance(selected);
      setAvailableBalance(selected ? String(selected.availableBalance) : '');
      setError(selected ? null : 'That leave type was not found for this employee.');
    } catch (caught) {
      setError(normalizeApiError(caught).message || 'Unable to load leave balance.');
    } finally {
      setLoading(false);
    }
  }, [route.params.employeeId, route.params.leaveTypeId, route.params.leaveYear]);

  useFocusEffect(useCallback(() => {
    load().catch(() => undefined);
  }, [load]));

  async function submit() {
    if (lock.current || !balance) return;
    const validation = validateLeaveBalanceAdjustment({ availableBalance, reason });
    if (validation) {
      setError(validation);
      return;
    }
    if (balance.isLocked) {
      setError('This leave balance is locked and cannot be updated.');
      return;
    }
    if (!balance.requiresBalance) {
      setError('This leave type does not use a tracked balance.');
      return;
    }
    lock.current = true;
    setSaving(true);
    setError(null);
    try {
      await runOperation('update', () => updateEmployeeLeaveBalance(route.params.employeeId, {
        leaveTypeId: route.params.leaveTypeId,
        leaveYear: route.params.leaveYear,
        availableBalance: Number(availableBalance),
        reason: reason.trim(),
      }));
      navigation.goBack();
    } catch (caught) {
      setError(normalizeApiError(caught).message || 'Unable to update leave balance.');
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }

  return (
    <PermissionGuard permission={PERMISSIONS.SETTINGS_UPDATE} fallback={<UnauthorizedScreen />}>
      <AppScreen webLayout="narrow" scroll>
        <ScreenHeader title="Update leave balance" onBack={navigation.goBack} />
        <View style={styles.body}>
          {loading ? <AppLoader message="Loading leave balance" /> : null}
          {!loading && error && !balance ? <ErrorState message={error} onRetry={load} /> : null}
          {data && balance ? (
            <>
              <Text style={[styles.title, { color: palette.textPrimary }]}>
                {data.employee.displayName}
              </Text>
              <Text style={[styles.meta, { color: palette.textSecondary }]}>
                {data.employee.employeeNumber} · {formatDisplayLabel(balance.leaveTypeName)} ({formatDisplayLabel(balance.leaveTypeCode)})
                {' · '}
                {route.params.leaveYear}
              </Text>
              <Text style={[styles.meta, { color: palette.textSecondary }]}>
                Current available: {formatLeaveBalanceValue(balance.availableBalance)}
                {' · '}Used: {formatLeaveBalanceValue(balance.debitedDays)}
                {' · '}Pending: {formatLeaveBalanceValue(balance.pendingLeaveDays)}
              </Text>
              <AppInput
                label="Available balance"
                value={availableBalance}
                onChangeText={setAvailableBalance}
                keyboardType="numbers-and-punctuation"
                placeholder="0.00"
              />
              <AppInput
                label="Reason"
                value={reason}
                onChangeText={setReason}
                placeholder="Why this balance is being changed"
              />
              {error ? (
                <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
                  {error}
                </Text>
              ) : null}
              <AppButton
                title="Save leave balance"
                onPress={submit}
                loading={saving}
                disabled={saving || balance.isLocked}
              />
            </>
          ) : null}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xl },
  title: typography.sectionTitle,
  meta: typography.body,
  error: typography.caption,
});
